import { useQuery } from '@apollo/client';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { readLinkedUserId } from '../../../paths';

import { ADMIN_LIST_USERS } from './AdminSelectUser';

import type { AdminListUsersQuery, AdminListUsersQueryVariables } from './__generated__/AdminSelectUser.gql';
import type { IAdminSelectUserSelection } from './AdminSelectUser';

interface ILinkableUser {
  id: string;
  email: string;
  family: { id: string };
}

/**
 * The selection a `?userId=` link stands for, built from that user's own
 * record, or null when the link names no user or one the list lacks. A
 * `familyId` in the URL is never read.
 */
export function linkedUserSelection(
  params: URLSearchParams,
  users: ReadonlyArray<ILinkableUser>
): IAdminSelectUserSelection | null {
  const userId = readLinkedUserId(params);
  const user = userId === null ? undefined : users.find((candidate) => candidate.id === userId);
  return user === undefined ? null : { userId: user.id, familyId: user.family.id, email: user.email };
}

/**
 * Seeds a picker's selection once from the `?userId=` link the scene mounted
 * with, as soon as the user list the picker shares has loaded. Returns true
 * while that seed is still waiting on the list, so a scene can hold work that
 * would otherwise run against the empty selection first.
 */
export function useSeedSelectionFromLink(onSeed: (selection: IAdminSelectUserSelection) => void): boolean {
  const [searchParams] = useSearchParams();
  const [linkParams] = React.useState(searchParams);
  const [isPending, setIsPending] = React.useState(() => readLinkedUserId(linkParams) !== null);
  const { data, error } = useQuery<AdminListUsersQuery, AdminListUsersQueryVariables>(ADMIN_LIST_USERS, {
    skip: readLinkedUserId(linkParams) === null,
  });
  const hasSeededRef = React.useRef(false);

  React.useEffect(() => {
    if (hasSeededRef.current || (data === undefined && error === undefined)) {
      return;
    }
    hasSeededRef.current = true;
    const selection = data === undefined ? null : linkedUserSelection(linkParams, data.adminListUsers);
    if (selection !== null) {
      onSeed(selection);
    }
    setIsPending(false);
  }, [data, error, linkParams, onSeed]);

  return isPending;
}
